import { Capacitor } from '@capacitor/core';
import { errorMessage } from './errorMessage';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { invokeFunction } from './invokeFunction';

/* =============================================================================
 * Files
 * -----------------------------------------------------------------------------
 * Every upload, view and delete in the app goes through here.
 *
 * Files live on ImageKit and are uploaded as private, which means a URL alone
 * fetches nothing — each view needs a fresh, short-lived signature minted
 * server-side after the database has agreed the caller may see that file.
 * Half of what this system stores is passports and Emirates IDs, so that
 * property is the whole point rather than a precaution.
 *
 * The browser never sees the ImageKit private key, never chooses the folder,
 * and never decides whether a file is private. All three are fixed in the Edge
 * Functions.
 * ========================================================================== */

export type FileKind =
  | 'document' | 'receipt' | 'avatar' | 'payslip' | 'vehicle' | 'project';

export interface UploadedFile {
  id: string;
  /** Store this. It is what every later view and delete is keyed on. */
  filePath: string;
  fileId: string;
  name: string;
  size: number;
  mime: string | null;
}

/**
 * What each kind of upload will accept, for the file picker's `accept`.
 *
 * The Edge Function holds the authoritative list — the browser cannot be
 * trusted about a MIME type and this one is only there to stop somebody
 * choosing a file that was always going to be refused. The two are compared by
 * fileKinds.test.ts, because a picker that offers what the server rejects is a
 * worse experience than one that offers less.
 */
export const ACCEPTED_MIME: Record<FileKind, string[]> = {
  document: [
    'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf',
    /* Spreadsheets, for an employee's own papers only.
       A man's settlement or leave calculation is worked out in Excel and has to
       be findable three years after he leaves — which is the point of keeping
       it against him rather than in a folder somebody has to remember. Not
       offered for receipts or vehicle papers, where a spreadsheet would be
       somebody attaching the wrong thing. */
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ],
  receipt: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'],
  avatar: ['image/jpeg', 'image/png', 'image/webp'],
  payslip: ['application/pdf'],
  vehicle: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'],
  project: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'],
};

/** What the size limits actually are, so a form can say so before uploading. */
export const MAX_UPLOAD_BYTES: Record<FileKind, number> = {
  document: 10 * 1024 * 1024,
  receipt: 10 * 1024 * 1024,
  avatar: 2 * 1024 * 1024,
  payslip: 10 * 1024 * 1024,
  /* Five, not ten. A registration card is a photograph of a card and an
     insurance certificate is a page — neither needs ten, and five vehicles'
     worth of paperwork on a site phone's data allowance is a real cost. */
  vehicle: 5 * 1024 * 1024,
  /* Ten, unlike a vehicle's. A building permit is a scanned multi-page PDF
     from an authority that has never heard of compression, and five turns it
     away. It is also uploaded once, from an office, rather than from a site
     phone on mobile data — so the cost falls somewhere that can bear it. */
  project: 10 * 1024 * 1024,
};

/**
 * Uploads one file and registers it.
 *
 * `prefix` only shapes the stored filename — `passport-1737...jpg` reads better
 * in the ImageKit media library than a bare timestamp when somebody is looking
 * for something six months later.
 */
export async function uploadFile(
  kind: FileKind,
  file: File,
  options: { employeeId?: string | null; prefix?: string } = {},
): Promise<UploadedFile> {
  const form = new FormData();
  form.append('file', file);
  form.append('kind', kind);
  if (options.employeeId) form.append('employeeId', options.employeeId);
  if (options.prefix) form.append('prefix', options.prefix);

  const result = await invokeFunction<UploadedFile>('file-upload', { body: form });
  if (!result?.filePath) throw new Error('The upload did not complete.');
  return result;
}

/**
 * Uploads a document attached to a company's application to join.
 *
 * Separate from uploadFile because it is filed against an application rather
 * than a company — the sender is in no company yet, which is the whole reason
 * they are applying. The same function on the server takes it, down a branch
 * that asks a narrower question: is this application yours, and is it still
 * open.
 */
export async function uploadRegistrationFile(
  registrationId: string,
  documentKind: 'trade_licence' | 'establishment_card' | 'vat_certificate' | 'other',
  file: File,
): Promise<UploadedFile> {
  const form = new FormData();
  form.append('file', file);
  form.append('kind', 'registration');
  form.append('registrationId', registrationId);
  form.append('documentKind', documentKind);

  const result = await invokeFunction<UploadedFile>('file-upload', { body: form });
  if (!result?.filePath) throw new Error('The upload did not complete.');
  return result;
}

/**
 * A URL that works for a few minutes.
 *
 * Not cached deliberately: the signature expires, and a stale one produces a
 * broken image rather than an error anybody can act on. Ask again each time.
 */
/* =============================================================================
 * Signed links, and holding on to them for slightly less time than they live
 * -----------------------------------------------------------------------------
 * Every file in this application is a private object. Reaching one means asking
 * file-access to sign a URL after RLS has agreed the caller may see it — so a
 * document link, or a face in a table, is a network round trip before it is
 * anything else.
 *
 * A table of two hundred employees is two hundred of those, and a search box
 * re-rendering on every keystroke would mint two hundred more per character.
 * The avatar component used to hold its own map to prevent that, which worked
 * and had two problems: nothing else got the benefit, and it never let go.
 *
 * -----------------------------------------------------------------------------
 * WHY AN ENTRY DIES BEFORE THE SIGNATURE DOES
 * -----------------------------------------------------------------------------
 * That map kept a URL for the life of the page. The signature on it lasted ten
 * minutes. So on anything left open — a kiosk, a dashboard on a wall — the
 * faces quietly turned back into initials after ten minutes and stayed that
 * way, because the cache kept confidently handing out a link the server had
 * stopped honouring. Nothing failed loudly; the pictures just went.
 *
 * An entry is dropped at eight tenths of its life here. The margin is the
 * point: a link handed out at 79% still has a fifth of its life left to be
 * fetched over a bad connection, which is not true of one handed out at 99%.
 *
 * The promise is cached rather than the URL, so twenty rows mounting in one
 * tick share a single request. A failure is not cached at all — a signature
 * that failed because the radio dropped must be retriable by whatever mounts
 * next, not remembered as broken for the rest of the session.
 * ========================================================================== */

interface SignedLink {
  url: Promise<string>;
  /** When this stops being handed out. Not when the signature expires. */
  goodUntil: number;
}

const signed = new Map<string, SignedLink>();

/* A ceiling, so a long session scrolling a large table does not grow a map
   without limit. Entries are cheap — a string and a number — but unbounded is
   unbounded, and the oldest is always the one closest to expiring anyway. */
const MAX_SIGNED = 400;

/** How much of a signature's life we are willing to hand out. */
const SAFE_FRACTION = 0.8;

function evictExpired(now: number): void {
  for (const [key, entry] of signed) {
    if (entry.goodUntil <= now) signed.delete(key);
  }

  // Still too many: drop from the front, which is insertion order and so is
  // also roughly expiry order.
  while (signed.size > MAX_SIGNED) {
    const oldest = signed.keys().next();
    if (oldest.done) break;
    signed.delete(oldest.value);
  }
}

export async function fileUrl(
  filePath: string,
  expireSeconds = 300,
): Promise<string> {
  /* Keyed by both, because the same file signed for five minutes and for ten
     are not interchangeable — handing a caller who asked for ten minutes a link
     with one minute left is exactly the bug this cache was written to stop. */
  const key = `${expireSeconds}:${filePath}`;
  const now = Date.now();

  const existing = signed.get(key);
  if (existing && existing.goodUntil > now) return existing.url;

  const pending = invokeFunction<{ url?: string }>('file-access', {
    body: { action: 'sign', filePath, expireSeconds },
  })
    .then((result) => {
      if (!result?.url) throw new Error('No link came back for that file.');
      return result.url;
    })
    .catch((error: unknown) => {
      signed.delete(key);
      throw error;
    });

  evictExpired(now);
  signed.set(key, {
    url: pending,
    goodUntil: now + expireSeconds * 1000 * SAFE_FRACTION,
  });

  return pending;
}

/**
 * Forgets the signed link for one path, at every expiry it was signed for.
 *
 * Called after a file is replaced or deleted. The path may be the same — a
 * profile picture written back to the same key — in which case the cached link
 * points at content that no longer exists, and a picture that will not appear
 * until the page is reloaded reads as a failed upload.
 */
export function forgetFile(filePath: string | null | undefined): void {
  if (!filePath) return;
  for (const key of signed.keys()) {
    if (key.endsWith(`:${filePath}`)) signed.delete(key);
  }
}

/**
 * Drops every signed link.
 *
 * For sign-out. A signature outlives the session that minted it, so a link left
 * in memory is a live handle on another person's document if the next thing to
 * happen on this device is somebody else signing in.
 */
export function forgetAllFiles(): void {
  signed.clear();
}

/**
 * Removes the file from ImageKit and its registry row.
 *
 * `missingIsFine` exists for the delete-the-record case. A document row whose
 * file has already gone — or which predates the move to ImageKit and so was
 * never registered — must not be undeletable. Refusing to remove the row
 * because the file is missing leaves a listing nobody can ever clear.
 */
export async function deleteFile(
  filePath: string,
  { missingIsFine = false } = {},
): Promise<void> {
  try {
    const result = await invokeFunction<{ ok?: boolean; warning?: string }>('file-access', {
      body: { action: 'delete', filePath },
    });
    if (result?.warning) console.warn(result.warning);
  } catch (caught) {
    const message = caught instanceof Error ? caught.message : String(caught);
    if (missingIsFine && /not available to you|no link came back/i.test(message)) {
      console.warn('File already gone, removing the record anyway:', filePath);
      return;
    }
    throw caught;
  }
}

/**
 * Opens a file for viewing. Returns the reason it could not be opened, or null.
 *
 * Two entirely different mechanisms, because the two platforms fail in opposite
 * ways.
 *
 * In a browser, a popup is only allowed while the click that caused it is still
 * being handled. Awaiting the signed URL first ends that window, so the tab is
 * opened empty *before* the await and pointed at the URL once it arrives.
 *
 * Inside the app there is no popup blocker and that trick does not work at all:
 * `window.open` in a Capacitor WebView has no tab to open and returns something
 * unusable, which is why View appeared to do nothing on the handset. The
 * Browser plugin hands the URL to Android properly.
 */
export async function openFile(filePath: string): Promise<string | null> {
  // Native first — no pre-opened tab, because there is nothing to pre-open.
  if (Capacitor.isNativePlatform()) return openOnTheDevice(filePath);

  // No `noopener` here, deliberately: with it, window.open returns null and we
  // lose the handle needed to navigate the tab once the URL arrives. The opener
  // is severed below instead, after the page has been pointed somewhere.
  const tab = window.open('', '_blank');

  // Painted immediately, because otherwise the user watches `about:blank` for
  // the length of a round trip and reasonably assumes something broke.
  paintPlaceholder(tab, 'Opening your document…');

  try {
    const url = await fileUrl(filePath);
    if (tab) {
      // Severing the reference back to this app before navigating to a URL on
      // somebody else's domain.
      tab.opener = null;
      tab.location.replace(url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    return null;
  } catch (caught) {
    const message = errorMessage(caught, 'open a document');
    // Say why in the tab as well as on the page behind it. Somebody who has
    // already switched to the new tab would otherwise see nothing at all.
    paintPlaceholder(tab, message, true);
    return message;
  }
}

/* =============================================================================
 * Viewing a document on the phone
 * -----------------------------------------------------------------------------
 * This handed the signed URL to Browser.open, which on Android is a Chrome
 * Custom Tab. The tab appeared and shut itself half a second later, dropping
 * the person back in the app having seen nothing — for every document in the
 * list, every time.
 *
 * Chrome for Android has no inline PDF viewer. Given a URL it will not render,
 * a Custom Tab hands it to the download manager and then finishes, because its
 * work is done: there is no page to stay open on. Nothing fails, nothing is
 * reported, and from the outside it looks exactly like a crash.
 *
 * The signed URL cannot be made to help. The file is what it is, and no header
 * makes Chrome display a PDF it cannot display.
 *
 * -----------------------------------------------------------------------------
 * SO THE BYTES GO TO THE PLATFORM INSTEAD
 * -----------------------------------------------------------------------------
 * The same route the Send button on these documents has always used, and the
 * reason that one works on a handset while View did not: fetch the file, write
 * it to the cache directory, and let Android open it with something that can
 * actually read it. See shareLoadedFile.
 *
 * Every type, not only PDFs. An image would survive a Custom Tab, and splitting
 * the two would mean one route nobody exercises until the day somebody uploads
 * a HEIC — which is precisely how this bug got in.
 *
 * The cost is honest and worth naming: Android offers a chooser rather than
 * opening the file outright, because @capacitor/share is ACTION_SEND and the
 * plugins installed here have no ACTION_VIEW between them. A file-opener plugin
 * would remove that tap; it would also be a native dependency and a rebuild,
 * which is a decision rather than a fix.
 * ========================================================================== */
async function openOnTheDevice(filePath: string): Promise<string | null> {
  /* Named from the stored path, so whatever opens it shows something better
     than "document". withExtension fills the suffix in from the path and the
     MIME type — and the receiving app needs it to choose a viewer at all. */
  const name = filePath.split('/').pop()?.split('?')[0] || 'document';

  const file = await fetchFileForSharing(filePath, name);
  if ('error' in file) return file.error;

  const outcome = await shareLoadedFile(file);

  /* Cancelled is a decision, not a fault: somebody opened the chooser and
     thought better of it, and colouring the View button red for that would be
     telling them their document is broken when they had just declined to look
     at it. */
  return outcome.kind === 'failed' ? outcome.message : null;
}

/* =============================================================================
 * Showing somebody their own paper, without handing them a link to it
 * -----------------------------------------------------------------------------
 * openFile above navigates a new tab to the signed ImageKit URL. That is right
 * for staff — they are handling other people's documents all day and need to
 * print, save and send them — and it is wrong for the one place where the
 * person looking is the person the document is about.
 *
 * The reason is what a URL in an address bar IS. It sits in browser history, it
 * survives a copy, and for the length of its signature anybody holding it can
 * fetch that passport scan from anywhere, signed in or not. Handing a worker
 * his own visa copy that way hands him something he can paste into WhatsApp,
 * and it will still open for whoever receives it.
 *
 * So this route never lets the ImageKit URL out. The signature is minted, used
 * once to fetch the bytes, and thrown away; what the page gets is a blob: URL,
 * which the browser scopes to the document that created it. Copied into another
 * tab it resolves to nothing. Copied into a message it is a dead string.
 *
 * -----------------------------------------------------------------------------
 * WHAT THIS DOES NOT DO, SAID PLAINLY
 * -----------------------------------------------------------------------------
 * It is not DRM and nothing here pretends to be. Anybody who can SEE a document
 * can photograph it, and anybody willing to open developer tools can read the
 * bytes out of the page. What this removes is the accidental case and the easy
 * one: there is no link to copy, nothing lands in history, and the signature is
 * dead within the minute whatever happens to it.
 *
 * The stronger version — proxying the bytes through the Edge Function so no
 * ImageKit signature is ever minted browser-side at all — is a real option and
 * a real cost, since every byte would then cross the function. It is not
 * needed for this and is written down here so the choice is visible.
 * ========================================================================== */

/**
 * How long the signature behind a private view is good for.
 *
 * The floor clampExpiry allows is thirty seconds; sixty leaves room for the
 * fetch to start over a bad connection without leaving anything usable lying
 * around afterwards. It is spent immediately either way — by the time the
 * viewer paints, the only handle on the file is a blob: URL.
 */
const PRIVATE_VIEW_SECONDS = 60;

export interface PrivateView {
  /** A blob: URL. Belongs to this document and dies with it. */
  url: string;
  mime: string;
  /**
   * MUST be called when the viewer closes.
   *
   * A blob: URL pins its bytes in memory until it is revoked, and a document is
   * a photograph of a passport — several megabytes each. A viewer opened thirty
   * times without revoking is thirty scans still resident.
   */
  revoke: () => void;
}

export async function viewPrivately(filePath: string): Promise<PrivateView> {
  const url = await fileUrl(filePath, PRIVATE_VIEW_SECONDS);

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`The file could not be fetched (${response.status}).`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  return {
    url: objectUrl,
    mime: blob.type || 'application/octet-stream',
    revoke: () => URL.revokeObjectURL(objectUrl),
  };
}

/**
 * Writes a holding page into the tab opened a moment ago.
 *
 * Inline styles and no assets: this document exists for a second or two and
 * must render before anything else could possibly load. Colours are fixed
 * rather than themed for the same reason — it cannot see the app's stylesheet.
 *
 * i18n-exempt: what the scanner finds in here is HTML — a doctype, a meta tag,
 * a font stack — and not one word of it is read by anybody. The only sentence
 * on the page is `message`, which arrives already translated from the caller.
 */
function paintPlaceholder(tab: Window | null, message: string, isError = false): void {
  if (!tab) return;
  try {
    tab.document.write(`
      <!doctype html>
      <html><head><meta charset="utf-8"><title>NoorOne ERP</title>
      <meta name="color-scheme" content="light dark">
      <style>
        body{margin:0;height:100vh;display:grid;place-items:center;
             font:15px/1.6 -apple-system,Segoe UI,Roboto,sans-serif;
             background:#12180f;color:#e8ece6;text-align:center;padding:24px}
        .m{max-width:34ch}
        .e{color:#ff9d9d}
      </style></head>
      <body><div class="m ${isError ? 'e' : ''}">${escapeHtml(message)}</div></body></html>
    `);
    tab.document.close();
  } catch {
    // Some browsers refuse document.write into a popup. The tab still works;
    // it just shows blank for a moment, which is where this started.
  }
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

/* =============================================================================
 * Sending a file to somebody
 * -----------------------------------------------------------------------------
 * A man's visa copy is wanted by a landlord, a policeman wants a Mulkiya, an
 * accountant wants one page of a permit. Today the only way out of this app is
 * to open the file and hope the viewer offers something — and on a phone the
 * export button gives the *list* of documents rather than the document.
 *
 * -----------------------------------------------------------------------------
 * The file, never the link
 * -----------------------------------------------------------------------------
 * The obvious implementation shares the signed URL, and it is wrong twice over.
 * That URL is a credential: anybody holding it reads the document without
 * signing in, and it would sit in a WhatsApp thread being forwarded. And it
 * expires in five minutes, so the recipient who opens it over lunch gets an
 * error and reports the app as broken.
 *
 * So the bytes are fetched and handed over as a file. A PDF arrives as a PDF and
 * a photograph as a photograph, which is what anybody expects and what the
 * receiving app needs to preview it.
 *
 * Where the browser cannot share files — a desktop, an older webview — it falls
 * back to saving rather than to sharing a link. Saving is worse than sharing and
 * far better than handing out a key.
 * ========================================================================== */

export type ShareOutcome =
  | { kind: 'shared' }
  | { kind: 'saved'; why?: string }
  /** Cancelled by the person, which is not a failure and must not read as one. */
  | { kind: 'cancelled' }
  /**
   * The bytes are in hand and the browser refused to share them without a fresh
   * tap. Hold the file and offer again — see the note on shareFile.
   */
  | { kind: 'needsTap'; file: File }
  | { kind: 'failed'; message: string };

/**
 * Fetches the bytes of a stored file, ready to be shared or saved.
 *
 * Separate from the sharing because of a rule that is easy to miss and
 * impossible to work around: navigator.share() requires *transient user
 * activation*. It has to be called inside the tap that asked for it, and three
 * awaits — signing the URL, fetching it, reading the blob — spend that
 * activation on network round trips. Chrome then refuses with
 *
 *     NotAllowedError: Permission denied
 *
 * which reads like a permissions problem with the file and is nothing of the
 * sort. The file is fine, the person is allowed, and the browser is objecting
 * that too much time passed since they touched the screen.
 */
export async function fetchFileForSharing(
  filePath: string,
  name: string,
): Promise<File | { error: string }> {
  try {
    const url = await fileUrl(filePath);
    const response = await fetch(url);
    if (!response.ok) {
      return { error: `The file could not be fetched (${response.status}).` };
    }

    const blob = await response.blob();
    const type = blob.type || 'application/octet-stream';
    return new File([blob], withExtension(name, filePath, type), { type });
  } catch (caught) {
    return { error: errorMessage(caught, 'fetch a document') };
  }
}

/* =============================================================================
 * A file has to be called something the receiving end can open
 * -----------------------------------------------------------------------------
 * The names built for sharing describe the file — "Noor Ul Amin — Visa copy" —
 * and describe it with no extension at all. Two consequences, and the first one
 * is what made sharing look broken:
 *
 *   Chrome refuses to share a file it cannot type. navigator.canShare says yes
 *   on the MIME type and then share() throws NotAllowedError, which reads as a
 *   permissions problem and is nothing of the sort.
 *
 *   And even where it went through, the recipient got a file WhatsApp could not
 *   preview and Windows could not open, because nothing told either what it was.
 *
 * The extension comes from the stored path first, since that is what was
 * actually uploaded, and from the MIME type when the path has none.
 * ========================================================================== */

const EXTENSION_FOR_MIME: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
};

export function withExtension(name: string, storedPath: string, mime: string): string {
  // Already named properly — a caller that passed "passport.pdf" is left alone.
  if (/\.[a-z0-9]{2,5}$/i.test(name)) return name;

  const fromPath = /\.([a-z0-9]{2,5})(?:\?|$)/i.exec(storedPath)?.[1]?.toLowerCase();
  const ext = fromPath ?? EXTENSION_FOR_MIME[mime.split(';')[0]!.trim()];

  /* No extension anywhere. Handing it over unnamed is still better than not
     handing it over, and the download path copes — so this does not throw. */
  return ext ? `${name}.${ext}` : name;
}

/** Saves a file the browser would not share. */
export function saveLoadedFile(file: File, why?: string): ShareOutcome {
  try {
    /* The object URL is revoked on a timer rather than immediately: revoking
       before the browser has started the download cancels it on some engines. */
    const href = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = href;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(href), 10_000);
    return { kind: 'saved', why };
  } catch (caught) {
    return { kind: 'failed', message: errorMessage(caught, 'save a document') };
  }
}

/**
 * Hands a file already in memory to whatever the person picks.
 *
 * Call this synchronously from a tap on the web. Nothing is awaited before
 * share(), so the activation is still live.
 *
 * -----------------------------------------------------------------------------
 * The app and the browser need entirely different mechanisms
 * -----------------------------------------------------------------------------
 * navigator.share is a Chrome feature, not a WebView one: Android's WebView —
 * which is what Capacitor runs — does not implement the Web Share API at all.
 * So on the phone the browser path finds nothing to call and falls through to
 * an <a download>, which a WebView also ignores without a native download
 * listener. Both routes dead-end silently, and the button appears to do
 * nothing however many times it is pressed.
 *
 * On the app the file is therefore written to the cache directory and handed to
 * the platform's own share sheet, which is what puts it in front of WhatsApp.
 * The user activation rule does not apply there, so the two-tap dance is a web
 * concern only.
 */
export async function shareLoadedFile(file: File): Promise<ShareOutcome> {
  if (Capacitor.isNativePlatform()) return shareNatively(file);

  if (typeof navigator.canShare !== 'function' || !navigator.canShare({ files: [file] })) {
    // A desktop browser. Saving is worse than sharing and far better than
    // handing out a signed URL that reads the document without signing in.
    return saveLoadedFile(file, 'This browser cannot share files, so it was saved instead.');
  }

  return navigator.share({ files: [file], title: file.name })
    .then((): ShareOutcome => ({ kind: 'shared' }))
    .catch((caught: unknown): ShareOutcome => {
      // Dismissing the sheet throws AbortError. Reporting that as a failure
      // would put an error on screen every time somebody changed their mind.
      if (caught instanceof Error && caught.name === 'AbortError') return { kind: 'cancelled' };

      /* Activation spent. The bytes are already here, so a second tap shares
         instantly — which is why this is its own outcome rather than a failure
         or a silent save. */
      if (caught instanceof Error && caught.name === 'NotAllowedError') {
        return { kind: 'needsTap', file };
      }

      return saveLoadedFile(file, caught instanceof Error ? caught.message : String(caught));
    });
}

/**
 * The app's own share sheet.
 *
 * The file has to exist somewhere the receiving app can read, so it is written
 * to the cache directory first — cache rather than documents, because this is a
 * copy made to be handed over and nobody should have to tidy it up afterwards.
 */
async function shareNatively(file: File): Promise<ShareOutcome> {
  try {
    const base64 = await toBase64(file);

    /* Named after the file. The name travels with it into WhatsApp, and
       "document.pdf" from four different people is four files nobody can tell
       apart. Slashes and colons are stripped because this becomes a real path
       on a real filesystem. */
    const safe = file.name.replace(/[\\/:*?"<>|]/g, '-').slice(0, 120) || 'document';

    const written = await Filesystem.writeFile({
      path: safe,
      data: base64,
      directory: Directory.Cache,
    });

    await Share.share({ title: file.name, url: written.uri });
    return { kind: 'shared' };
  } catch (caught) {
    const message = caught instanceof Error ? caught.message : String(caught);

    // Dismissing the sheet is not a failure and must not read as one. Android
    // words this several ways, none of them an error type worth matching on.
    if (/cancel/i.test(message) || /abort/i.test(message)) return { kind: 'cancelled' };

    return { kind: 'failed', message };
  }
}

/** A file as base64, which is the only shape Filesystem.writeFile accepts. */
function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
    reader.onload = () => {
      const result = String(reader.result);
      // readAsDataURL gives "data:<mime>;base64,<payload>" and the plugin wants
      // only the payload.
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Fetch and share in one go.
 *
 * Works where the fetch is quick enough to stay inside the activation window.
 * Where it is not, the caller is handed the file back and asks for one more tap.
 */
export async function shareFile(filePath: string, name: string): Promise<ShareOutcome> {
  const file = await fetchFileForSharing(filePath, name);
  if ('error' in file) return { kind: 'failed', message: file.error };
  return shareLoadedFile(file);
}

/* =============================================================================
 * Handing over something the app just made
 * -----------------------------------------------------------------------------
 * Every export in this application built its own object URL, made a detached
 * anchor, clicked it and revoked the URL on the next line. Eight of them: the
 * WPS file, every Excel export, the payslip PDFs, the platform backup, the
 * attendance grid, the import error report.
 *
 * All three of those steps are wrong, and the file above says so:
 *
 *   the anchor is never in the document   some engines ignore a click on a node
 *                                         that is not in the tree
 *   the URL is revoked on the next line   revoking before the browser has begun
 *                                         the download cancels it
 *   there is no native path at all        Android's WebView, which is what
 *                                         Capacitor runs, ignores <a download>
 *                                         and does not implement Web Share
 *
 * The last one is why the WPS button appeared to do nothing on a phone however
 * many times it was pressed. shareLoadedFile has handled all three since it was
 * written for employee documents; no export ever used it.
 * ========================================================================== */

/**
 * Saves or shares something generated in the browser.
 *
 * @param body The bytes. A string for text formats, an ArrayBuffer for the
 *   binary ones — both are what the generators here already produce.
 */
export async function exportGeneratedFile(
  body: BlobPart,
  name: string,
  mime: string,
): Promise<ShareOutcome> {
  const file = new File([body], name, { type: mime });

  /* =========================================================================
   * In a browser this downloads. It does not offer to share.
   * -------------------------------------------------------------------------
   * This went through shareLoadedFile, which asks navigator.share first where
   * the browser supports it. That is right for a document FileLink has just
   * fetched — somebody tapping a passport scan on a phone usually means to send
   * it to somebody. It is wrong for a file this application just built.
   *
   * Pressing Excel or PDF is a request for the file, not a request to pick an
   * app to give it to, and answering with a share sheet puts a decision in
   * front of somebody who had already made theirs. On a desktop it is worse
   * still: the Windows share sheet often has nothing useful in it for an .xlsx,
   * so the button appeared to open a menu about nothing.
   *
   * It also removes a whole class of failure. navigator.share needs a live user
   * activation, and every export here awaits a chunk load, a query and a
   * document build first — so the activation was always spent, share() always
   * rejected, and the file was only saved because of a fallback. Not asking is
   * simpler than recovering.
   *
   * -------------------------------------------------------------------------
   * THE APP IS THE ONE PLACE THAT STILL SHARES
   * -------------------------------------------------------------------------
   * Android's WebView ignores `<a download>` entirely — that is the whole
   * reason files.ts exists — so on a device there is nothing to fall back to.
   * shareLoadedFile writes the file to the cache directory and hands it to the
   * platform's own sheet, which is how a file gets out of the app at all. The
   * user-activation rule does not apply there, so nothing above is a problem.
   * ====================================================================== */
  if (Capacitor.isNativePlatform()) return shareLoadedFile(file);

  return saveLoadedFile(file);
}

/** The two the generators in this codebase produce. */
export const EXPORT_MIME = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf',
  csv: 'text/csv;charset=utf-8',
  text: 'text/plain;charset=utf-8',
  json: 'application/json',
} as const;
